'use client'

import ArticleHeader from '@/components/ArticleHeader'
import VideoPlayer from '@/components/VideoPlayer'
import useInView from '@/lib/hooks/useInView'
import { urlForImage } from '@/sanity/lib/image'
import { PortableText } from '@portabletext/react'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'

interface ArticleContentProps {
	article: any
}

export default function ArticleContentStory({ article }: ArticleContentProps) {
	const leadImage = article?.leadImage || article?.image
	const [activeImage, setActiveImage] = useState<string | null>(leadImage ? 'cover' : null)

	const ArticleContentComponents = {
		types: {
			image: ({ value }) => (
				<ArticleImageInline image={value} setActiveImage={setActiveImage} identifier={value?._key} />
			),
			video: ({ value }) => {
				console.log('value:', value)
				return (
					<div className={`relative w-full my-[6rem]`}>
						<VideoPlayer
							playbackId={value?.video?.asset?.playbackId}
							className={`block w-full h-auto`}
							autoplay={false}
							showAudioToggle={true}
							loop={false}
							posterTime={value?.posterTime}
						/>
					</div>
				)
			}
		},
		block: {
			h2: ({ children }: any) => (
				<h2 className="style-body--xl md:text-center mt-[4rem] md:mt-[6rem] mb-[3rem] md:mb-[5rem]">{children}</h2>
			),
			h4: ({ children }: any) => <h4 className="style-h4 pt-6 mb-6">{children}</h4>,
			normal: ({ children }: any) => <p className="style-body mb-6">{children}</p>,
			blockquote: ({ children }: any) => <blockquote className="blockquote">{children}</blockquote>
		},
		marks: {
			link: ({ children, value }: any) => {
				const target = !value?.url?.startsWith('/') ? '_blank' : '_self'
				const rel = !value?.url?.startsWith('/') ? 'noreferrer noopener' : ''
				return target === '_blank' ? (
					<a href={value?.url} className="text-current hover:text-blue" rel={rel} target={target}>
						{children}
					</a>
				) : (
					<Link href={value?.url} className="text-current hover:text-blue" rel={rel} target={target}>
						{children}
					</Link>
				)
			}
		}
	}

	const desktopImageClasses = `absolute inset-0 w-full h-full bg-white`

	return (
		<div
			className={`relative w-full sm:site-grid-span-10 px-site-margin md:w-full mx-auto md:grid grid-cols-2 gap-gutter items-start`}>
			<div className={`relative md:pr-4 lg:pr-[calc((var(--col-width)*2)+var(--gutter)*2)] rich--text-content`}>
				<ArticleHeader
					className={`mb-4 md:mb-20 ${article?.layout === 'pictures' && 'md:mb-[10rem] text-center'}`}
					title={article?.title}
					excerpt={article?.excerpt}
				/>

				<ArticleImageInline image={leadImage} setActiveImage={setActiveImage} identifier="cover" />

				<PortableText value={article?.content} components={ArticleContentComponents} />
			</div>

			<div
				className={`hidden md:block article-images sticky top-[calc(var(--header-height)_+_8rem)] h-[calc(100vh-var(--header-height)-16rem)] bg-white`}>
				<div
					className={`${desktopImageClasses} ${activeImage == 'cover' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
					<ArticleImageScrollControlled image={leadImage} />
				</div>

				<PortableText
					value={article?.content?.filter((block: any) => block._type === 'image')}
					components={{
						types: {
							image: ({ value }) => (
								<div
									className={`${desktopImageClasses} ${activeImage == value?._key ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
									<ArticleImageScrollControlled image={value} />
								</div>
							)
						}
					}}
				/>
			</div>
		</div>
	)
}

interface ArticleImageInlineProps {
	image: any
	setActiveImage: React.Dispatch<React.SetStateAction<string>>
	identifier: string
}

function ArticleImageInline({ image, setActiveImage, identifier }: ArticleImageInlineProps) {
	const [ref, isInView] = useInView({
		rootMargin: '-40% 0% -50% 0%',
		threshold: 0
	})

	useEffect(() => {
		if (isInView) {
			setActiveImage(identifier)
		}
	}, [isInView])

	return (
		<>
			<div className={`article-image md:hidden w-full my-[6rem]`}>
				<Image
					className={`w-full h-auto md:h-full md:object-contain md:object-top`}
					src={urlForImage(image).url()}
					width={image?.asset?.metadata?.dimensions?.width}
					height={image?.asset?.metadata?.dimensions?.height}
					sizes="(min-width: 712px) 40vw, 100vw"
					alt={image?.alt || ''}
					placeholder="blur"
					blurDataURL={image?.asset?.metadata?.lqip}
				/>
				{image?.caption && <p className="style-caption mt-6">{image?.caption}</p>}
			</div>
			<div className={`invisible size-[1px] ${isInView ? 'bg-success' : 'bg-error'}`} ref={ref} />
		</>
	)
}

interface ArticleImageScrollControlledProps {
	image: any
}

function ArticleImageScrollControlled({ image }: ArticleImageScrollControlledProps) {
	return (
		<div className="absolute w-full h-full flex flex-col items-center justify-center inset-center md:pl-[calc((var(--col-width)*1)+var(--gutter)*1)] lg:pl-8">
			<Image
				className={`w-full h-auto max-h-full object-contain object-top`}
				src={urlForImage(image).url()}
				width={image?.asset?.metadata?.dimensions?.width}
				height={image?.asset?.metadata?.dimensions?.height}
				sizes="(min-width: 712px) 40vw, 100vw"
				alt={image?.alt || ''}
				placeholder="blur"
				blurDataURL={image?.asset?.metadata?.lqip}
			/>
			{image?.caption && (
				<p className="style-caption mt-6 [@media_(max-height:1000px)]:text-center">{image?.caption}</p>
			)}
		</div>
	)
}
